/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isEmpty from 'lodash/isEmpty';

import { fetchTourTimes, clearTourTimes } from './tourTimesActions';

export const SET_DATE_FILTER = 'SET_DATE_FILTER';
export const SET_FILTERED_TOUR_LIST = 'SET_FILTERED_TOUR_LIST';
export const SET_IS_FILTER_MODAL_OPEN = 'SET_IS_FILTER_MODAL_OPEN';
export const CLEAR_FILTERS = 'CLEAR_FILTERS';
export const SET_GUEST_COUNT = 'SET_GUEST_COUNT';
export const SET_TOUR_TIME_FILTER = 'SET_TOUR_TIME_FILTER';

export function clearFilters() {
    return {
        type: CLEAR_FILTERS,
    };
}

export function setIsFilterModalOpen(isFilterModalOpen) {
    return {
        type: SET_IS_FILTER_MODAL_OPEN,
        payload: isFilterModalOpen,
    };
}

export function setFilteredTourList(tourIds) {
    return {
        type: SET_FILTERED_TOUR_LIST,
        payload: tourIds,
    };
}

function sortToursAlphabetically(a, b, tourMap) {
    if (tourMap?.[a]?.tour_name > tourMap?.[b]?.tour_name) {
        return 1;
    }
    if (tourMap?.[a]?.tour_name < tourMap?.[b]?.tour_name) {
        return -1;
    }
    return 0;
}

function sortTourList(
    availableTourIds = [],
    notYetReleasedTourIds = [],
    firstComeFirstServedTourIds = [],
    unavailableTourIds = [],
    tourMap = []
) {
    const filteredTourIds = [];

    filteredTourIds.push(
        ...availableTourIds.sort((a, b) => sortToursAlphabetically(a, b, tourMap))
    );

    filteredTourIds.push(
        ...firstComeFirstServedTourIds.sort((a, b) =>
            sortToursAlphabetically(a, b, tourMap)
        )
    );

    filteredTourIds.push(
        ...notYetReleasedTourIds.sort((a, b) => sortToursAlphabetically(a, b, tourMap))
    );

    filteredTourIds.push(
        ...unavailableTourIds.sort((a, b) => sortToursAlphabetically(a, b, tourMap))
    );

    return filteredTourIds;
}

function filterActiveTours(tourIds, tourMap) {
    return tourIds?.filter((tourId) => tourMap[tourId]) ?? [];
}

function filterTourByAvailability(tourId, tourTimesData, guestCount) {
    let hasEnoughAvailable = false;
    const times = tourTimesData[tourId];

    if (!isEmpty(times)) {
        times.forEach((time) => {
            if (time.availableCount >= guestCount || time.unreleasedCount >= guestCount) {
                hasEnoughAvailable = true;
            }
        });
    }

    return hasEnoughAvailable;
}

export function filterTourList() {
    return (dispatch, getState) => {
        const date = getState().timedEntry.filters.date;
        const guestCount = getState().timedEntry.filters.guestCount;
        const hasTourTimeFilter = getState().timedEntry.filters.hasTourTimeFilter;
        const tourTimesData = getState().timedEntry.tourTimes.data;
        const filteredTourTimes = getState().timedEntry.tourTimes.filteredTourTimes;
        const facilityAvailability = getState().timedEntry.facilityAvailability;
        const tourMap = getState().timedEntry.tours.tourMap;
        const tourList = getState().timedEntry.tours.tourList;
        const monthKey = date?.format('YYYY-MM');
        const dateKey = date?.format('YYYY-MM-DD');
        let availableTourIds = [];
        let notYetReleasedTourIds = [];
        let firstComeFirstServedTourIds = [];
        let unavailableTourIds = [];

        const isAvailable =
            !!facilityAvailability.isAvailableByDate?.[monthKey]?.[dateKey];
        const isNotReleased =
            !!facilityAvailability.isNotReleasedByDate?.[monthKey]?.[dateKey];

        if (hasTourTimeFilter) {
            const filteredTourTimesKeys = Object.keys(filteredTourTimes);
            filteredTourTimesKeys.forEach((key) => {
                let isTourAvailable = false;
                let isTourNotReleased = false;

                filteredTourTimes?.[key]?.forEach((timeItem) => {
                    if (timeItem?.isAvailable) {
                        isTourAvailable = true;
                        isTourNotReleased = false;
                    }

                    if (!isTourAvailable && timeItem?.unreleasedCount) {
                        isTourNotReleased = true;
                    }
                });

                if (isTourAvailable) {
                    availableTourIds?.push(key);
                }

                if (isTourNotReleased) {
                    notYetReleasedTourIds?.push(key);
                }
            });
        }

        if (!hasTourTimeFilter && (isAvailable || isNotReleased)) {
            if (isAvailable) {
                availableTourIds.push(
                    ...filterActiveTours(
                        facilityAvailability.availableTourIdsByDate?.[monthKey]?.[
                            dateKey
                        ],
                        tourMap
                    )
                );
            }
            if (isNotReleased) {
                const toursToInclude = filterActiveTours(
                    facilityAvailability.notReleasedTourIdsByDate?.[monthKey]?.[dateKey],
                    tourMap
                ).filter((tourId) => !availableTourIds.includes(tourId));

                notYetReleasedTourIds.push(...toursToInclude);
            }

            if (!isEmpty(tourTimesData) && guestCount) {
                if (!isEmpty(availableTourIds)) {
                    availableTourIds = availableTourIds.filter((tourId) =>
                        filterTourByAvailability(tourId, tourTimesData, guestCount)
                    );
                }
                if (!isEmpty(notYetReleasedTourIds)) {
                    notYetReleasedTourIds = notYetReleasedTourIds.filter((tourId) =>
                        filterTourByAvailability(tourId, tourTimesData, guestCount)
                    );
                }
            }
        }

        const tourListIds = tourList?.map((tour) => tour?.tour_id);

        firstComeFirstServedTourIds = filterActiveTours(tourListIds, tourMap).filter(
            (tourId) =>
                facilityAvailability.firstComeFirstServedToursByDate?.[monthKey]?.[
                    dateKey
                ]?.[tourId] &&
                !availableTourIds.includes(tourId) &&
                !notYetReleasedTourIds.includes(tourId)
        );

        unavailableTourIds = filterActiveTours(tourListIds, tourMap).filter(
            (tourId) =>
                !availableTourIds.includes(tourId) &&
                !notYetReleasedTourIds.includes(tourId) &&
                !firstComeFirstServedTourIds.includes(tourId)
        );

        const filteredTourIds = sortTourList(
            availableTourIds,
            notYetReleasedTourIds,
            firstComeFirstServedTourIds,
            unavailableTourIds,
            tourMap
        );

        dispatch(setFilteredTourList(filteredTourIds));
    };
}

export function setDateFilter(date, shouldFilterTours = true) {
    return (dispatch, getState) => {
        const facilityId = getState().timedEntry.facility?.facility_id;
        dispatch({
            type: SET_DATE_FILTER,
            payload: date,
        });

        if (date && facilityId) {
            dispatch(
                fetchTourTimes({
                    facilityId,
                    date: date.format('YYYY-MM-DD'),
                    shouldFilterTours,
                })
            );
        } else {
            dispatch(clearTourTimes());
        }

        if (shouldFilterTours) {
            dispatch(filterTourList());
        }
    };
}

export function setTourTimeFilter(tourTimeFilters) {
    return {
        type: SET_TOUR_TIME_FILTER,
        payload: tourTimeFilters,
    };
}
